import {
  BaseFilterType,
  EmployeeFilterType,
  EmployeesService,
  ShortEmployeeItemResponse,
  useDebounce,
} from '@innowise-group/core';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './employe.styles';
import { FilterButton } from '../shared';
import { Loader, DropdownListItem } from '@innowise-group/mui-kit';
import { EmployeeTypes } from '@constants';
import { localizedNameObject } from '@innowise-group/utilities';

interface EmployeFilterProps extends Pick<BaseFilterType, 'labelKey' | 'isOpen'> {
  state: EmployeeFilterType['state'];
  pinned?: boolean;
  onlyActual?: boolean;
  employeeType?: EmployeeTypes;
  onValueChange: (employeId: EmployeeFilterType['state']) => void;
  onToggleFilter: () => void;
  onRemoveFilter: (state: EmployeeFilterType['state']) => void;
  onClickFilterOutside: () => void;
}

const EmployeFilter: FC<EmployeFilterProps> = ({
  state,
  labelKey,
  isOpen,
  pinned,
  onlyActual = true,
  employeeType,
  onValueChange,
  onToggleFilter,
  onRemoveFilter,
  onClickFilterOutside,
}) => {
  const ref = useRef(null);
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [employees, setEmployees] = useState<ShortEmployeeItemResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentEmployee, setCurrentEmployee] = useState<ShortEmployeeItemResponse | null>(null);
  const [debouncedValue] = useDebounce(search);

  const getEmployees = useCallback(
    async (partOfName: string) => {
      setIsLoading(true);
      try {
        const { data } = await EmployeesService.searchEmployees({ partOfName, size: 5, onlyActual, employeeType });
        setEmployees(data.content);
      } finally {
        setIsLoading(false);
      }
    },
    [EmployeesService.searchEmployees, onlyActual, employeeType],
  );

  const getCurrentEmployee = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const { data } = await EmployeesService.getEmployeeById(id);
      setCurrentEmployee(data);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const [currentEmployeeFromState] = state;
    if (currentEmployeeFromState) {
      getCurrentEmployee(currentEmployeeFromState);
    }
  }, []);

  useEffect(() => {
    if (!state.length) setCurrentEmployee(null);
  }, [state]);

  useEffect(() => {
    if (debouncedValue) {
      getEmployees(debouncedValue);
    } else {
      setEmployees([]);
    }
  }, [debouncedValue, getEmployees]);

  const onInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
  };

  const onRemoveFilterHandler = () => onRemoveFilter([]);

  const onClickFilterOutsideHandle = () => {
    const isEmpty = !state?.length;
    setSearch('');
    if (isEmpty && !pinned) onRemoveFilterHandler();
    onClickFilterOutside();
  };

  useEffect(() => {
    setAnchorEl(isOpen ? ref.current : null);
  }, [isOpen]);

  const onValueChangeHandler = (employee: ShortEmployeeItemResponse) => () => {
    onValueChange([employee.employeeId]);
    setCurrentEmployee(employee);
    setSearch('');
    if (employee.employeeId) onClickFilterOutside();
  };

  return (
    <Styled.Container ref={ref}>
      <FilterButton
        isActive={isOpen}
        action="delete"
        iconSize={16}
        iconViewBox="-1 -1 14 14"
        onToggleFilter={!pinned ? onRemoveFilterHandler : null}
        onClick={onToggleFilter}
      >
        <Styled.SelectedInfoContainer>
          <Styled.FirstSelectedValue>{t(labelKey)}</Styled.FirstSelectedValue>
          {currentEmployee && (
            <Styled.SelectedCount>{localizedNameObject(currentEmployee).localizedFullName}</Styled.SelectedCount>
          )}
        </Styled.SelectedInfoContainer>
      </FilterButton>
      <Styled.CustomAnimationWrapper
        disablePortal={true}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={onClickFilterOutsideHandle}
      >
        <Styled.FilterWrapper>
          <Styled.Input
            placeholder={t('pages.candidates.candidateCreation.selectTheEmployee')}
            onChange={onInputChangeHandler}
            value={search}
          />
          <Styled.ListItemContiner>
            {!isLoading ? (
              employees.map((employee) => {
                return (
                  <DropdownListItem key={employee.employeeId} onClick={onValueChangeHandler(employee)} selected={false}>
                    {employee.photoId || employee.miniPhotoHrmUrl ? (
                      <Styled.Image id={employee.photoId} miniPhotoUrl={employee.miniPhotoHrmUrl} />
                    ) : (
                      <Styled.NoImageContainer>
                        <Styled.NoImageIcon type="u_camera" size={20} viewBox="0 0 49 44" />
                      </Styled.NoImageContainer>
                    )}
                    <Styled.ItemText>{localizedNameObject(employee).localizedFullName}</Styled.ItemText>
                  </DropdownListItem>
                );
              })
            ) : (
              <Loader />
            )}
          </Styled.ListItemContiner>
        </Styled.FilterWrapper>
      </Styled.CustomAnimationWrapper>
    </Styled.Container>
  );
};

export default memo(EmployeFilter);
